/**
 * Block 12 PR-D-A Phase D1 Step 1 — Quote (КП) state types.
 *
 * Замещает QuoteStub. Source of truth UI — `docs/prototypes/screen-2.12.10-cp.html`.
 * Source of truth scope — `docs/prd/v4/10-block-12-calculator.md` §10.12.10.
 *
 * Phase D5 (Steps 8-11) расширит typing для draft persistence + Anketa→Quote seeding;
 * на Step 1 фиксируем минимальный shape для Container/Presenter wiring + reducer skeleton.
 */

/** Одна строка раздела «Состав» (17 default + custom). */
export interface CompositionRow {
  /**
   * Stable ID — default rows используют kebab-case slug'и
   * (`countertop`, `facades`, …, `metallic-legs` — см. `compositionRows.ts`),
   * custom rows — `genUuid()` (см. `@/lib/uuid`).
   */
  id: string;
  /** Название позиции (e.g. «Столешница»). */
  name: string;
  /** Тип материала/исполнения (e.g. «Камень кварц»). */
  itemType: string;
  /** Бренд (e.g. «Blum»). */
  brand: string;
  /**
   * PR-H1 Round 3 — marker для default rows seeded из Anketa freeItems (toggle
   * items: vitrine, basket-pullout, foldable-facade, trash-bin, laundry-basket,
   * metallic-legs, facade-radius fallback). Filter (isCompositionRowEmpty)
   * пропускает seeded rows даже с пустыми itemType/brand — visible в КП с
   * empty cells, ожидая ручного заполнения. Default rows без seed и custom
   * rows этот marker НЕ имеют (определяется только seedFromAnketa).
   */
  seeded?: boolean;
}

/** Одна строка раздела «Условия» — label + value (+ optional unit). */
export interface ConditionRow {
  id: string;
  label: string;
  value: string;
  unit?: 'дни' | 'мес' | '%' | '₸' | string;
}

/** Одна строка раздела «Контакты исполнителя» — свободный label + value. */
export interface ContactRow {
  id: string;
  label: string;
  value: string;
}

/**
 * Status hydration cascade Phase D5 Step 9:
 * `pending` → пока идёт Zustand/IDB/anketa-seed cascade; UI показывает skeleton.
 * `ready` → state доступен для редактирования и autosave.
 */
export type QuoteHydrationStatus = 'pending' | 'ready';

export interface QuoteState {
  /** Source projectId если КП создан из калькулятора (?projectId=<uuid>). null для standalone. */
  projectId: string | null;
  /** Выбранный тип мебели (из FURNITURE_TYPES либо custom). null до first выбора. */
  furnitureType: string | null;
  /** Состав 17 default + custom rows. */
  compositionRows: CompositionRow[];
  /** Стоимость с маржой (₸). null до first ввода. */
  costWithMargin: number | null;
  /** Условия. */
  conditions: ConditionRow[];
  /** Контакты исполнителя. */
  executorContacts: ContactRow[];
  /** Свободные комментарии. */
  comments: string;
  /**
   * Hydration cascade status (Phase D5). `initialQuoteState` стартует с `pending`;
   * Container переводит в `ready` после Zustand/IDB/anketa-seed cascade
   * (Phase D5 Step 9). Phase D1 Container делает синхронный flip без cascade.
   */
  hydrationStatus: QuoteHydrationStatus;
  /**
   * PR-H2 ADR-0027 — timestamp последнего seedFromAnketa (Date.now()).
   * undefined = legacy draft до PR-H2 deploy → Priority 2.6 invalidation check skipped.
   */
  seededAt?: number;
  /**
   * PR-H2 ADR-0027 — `project.updatedAt` (Anketa) на момент последнего seed.
   * QuotePageContainer Priority 2.6 сравнивает с current — если anketa newer,
   * dispatch PARTIAL_RESEED для refresh anketa-derived полей.
   */
  lastAnketaUpdatedAt?: number;
  /**
   * PR-H4 — название компании / имя исполнителя (например, «ЛучшаяМебель»).
   * Отображается отдельной секцией выше «Тип мебели» в UI и в верхнем левом
   * углу PDF header. Optional для backward-compat с SavedQuote, созданными
   * до PR-H4 deploy (undefined → пустой input, пустой top-left в PDF).
   * Preserved в PARTIAL_RESEED — не перезаписывается из anketa.
   */
  companyName?: string;
  /**
   * PR-D2 (ADR-0040) — хранимая ставка «Аванс, %» (0–100). Когда задана валидным
   * числом → сумма аванса в строке-условии «Размер аванса» = производное
   * `resolveAdvanceAmount(costWithMargin, advancePercent, manualRaw)`; поле суммы
   * read-only в UI (Вариант A). `null`/`undefined` → ручной режим по значению
   * строки-условия `advance`. Form-state поле (НЕ frozen Zod — `quote.ts`
   * валидирует серверный payload, не состояние формы); персистится целым
   * structured-clone в обоих IDB (`quoteDraftIdbSync`/`savedQuotesIdb`),
   * version bump не нужен. Preserved в PARTIAL_RESEED (per-КП, не из anketa).
   */
  advancePercent?: number | null;
}

export const initialQuoteState: QuoteState = {
  projectId: null,
  furnitureType: null,
  compositionRows: [],
  costWithMargin: null,
  conditions: [],
  executorContacts: [],
  comments: '',
  hydrationStatus: 'pending',
};
